import React, { useState, useEffect } from "react";
import styles from "./SalaryPanel.module.css";

export default function SalaryPanel({ onItemsChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "",
    totalSalary: "",
    incomeTax: "",
    localTax: "",
    health: "",
    care: "",
    pension: "",
    employment: "",
  });

  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState({});

  const comma = (n) =>
    n ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
  const uncomma = (n) => n.replace(/,/g, "");

  // ✅ 합계 자동 계산
  const calcTotal = (totalSalary, incomeTax, localTax, health, care, pension, employment) => {
    return totalSalary - incomeTax - localTax - health - care - pension - employment;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (['totalSalary', 'incomeTax', 'localTax', 'health', 'care', 'pension', 'employment'].includes(name)) {
      setForm({
        ...form,
        [name]: comma(value.replace(/,/g, "")),
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name === "employment") {
      e.preventDefault();
      addItem();
    }
  };

  const addItem = () => {
    const totalSalary = parseInt(uncomma(form.totalSalary));
    const incomeTax = parseInt(uncomma(form.incomeTax)) || 0;
    const localTax = parseInt(uncomma(form.localTax)) || 0;
    const health = parseInt(uncomma(form.health)) || 0;
    const care = parseInt(uncomma(form.care)) || 0;
    const pension = parseInt(uncomma(form.pension)) || 0;
    const employment = parseInt(uncomma(form.employment)) || 0;

    if (!form.name) return alert("이름을 입력하세요");
    if (isNaN(totalSalary)) return alert("급여 합계를 입력하세요");

    const total = calcTotal(totalSalary, incomeTax, localTax, health, care, pension, employment);

    const newItem = {
      name: form.name,
      totalSalary,
      incomeTax,
      localTax,
      health,
      care,
      pension,
      employment,
      total,
    };

    setItems([...items, newItem]);
    onItemsChange && onItemsChange([...items, newItem]);

    setForm({
      name: "",
      totalSalary: "",
      incomeTax: "",
      localTax: "",
      health: "",
      care: "",
      pension: "",
      employment: "",
    });
  };

  const removeItem = (idx) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    const updated = items.filter((_, i) => i !== idx);
    setItems(updated);
    onItemsChange && onItemsChange(updated);
  };

  const sumTotalSalary = items.reduce((a, b) => a + b.totalSalary, 0);
  const sumIncomeTax = items.reduce((a, b) => a + b.incomeTax, 0);
  const sumLocalTax = items.reduce((a, b) => a + b.localTax, 0);
  const sumHealth = items.reduce((a, b) => a + b.health, 0);
  const sumCare = items.reduce((a, b) => a + b.care, 0);
  const sumPension = items.reduce((a, b) => a + b.pension, 0);
  const sumEmployment = items.reduce((a, b) => a + b.employment, 0);
  const sumTotal = items.reduce((a, b) => a + b.total, 0);

  return (
    <div className={styles.sidePane}>
      <div className={styles.scrollWrap}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>이름</th>
              <th>급여 합계</th>
              <th>소득세</th>
              <th>지방소득세</th>
              <th>건강보험</th>
              <th>요양보험</th>
              <th>국민연금</th>
              <th>고용보험</th>
              <th>실지급액(자동)</th>
              <th>추가</th>
            </tr>
          </thead>
          <tbody>
            {/* 입력행 */}
            <tr className={styles.inputRow}>
              <td><input name="name" value={form.name} onChange={handleChange} /></td>
              <td><input name="totalSalary" value={form.totalSalary} onChange={handleChange} /></td>
              <td><input name="incomeTax" value={form.incomeTax} onChange={handleChange} /></td>
              <td><input name="localTax" value={form.localTax} onChange={handleChange} /></td>
              <td><input name="health" value={form.health} onChange={handleChange} /></td>
              <td><input name="care" value={form.care} onChange={handleChange} /></td>
              <td><input name="pension" value={form.pension} onChange={handleChange} /></td>
              <td><input name="employment" value={form.employment} onChange={handleChange} onKeyDown={handleKeyDown} /></td>
              <td className={styles.calcCell}>
                {comma(
                  form.totalSalary ? 
                  calcTotal(
                    parseInt(uncomma(form.totalSalary)) || 0,
                    parseInt(uncomma(form.incomeTax)) || 0,
                    parseInt(uncomma(form.localTax)) || 0,
                    parseInt(uncomma(form.health)) || 0,
                    parseInt(uncomma(form.care)) || 0,
                    parseInt(uncomma(form.pension)) || 0,
                    parseInt(uncomma(form.employment)) || 0
                  ) : 0
                )}
              </td>
              <td><button onClick={addItem}>추가</button></td>
            </tr>
            
            {/* 데이터 행들 */}
            {items.map((item, idx) => (
              <tr key={idx}>
                <td>{item.name}</td>
                <td>{comma(item.totalSalary)}</td>
                <td>{comma(item.incomeTax)}</td>
                <td>{comma(item.localTax)}</td>
                <td>{comma(item.health)}</td>
                <td>{comma(item.care)}</td>
                <td>{comma(item.pension)}</td>
                <td>{comma(item.employment)}</td>
                <td>{comma(item.total)}</td>
                <td><button onClick={() => removeItem(idx)}>삭제</button></td>
              </tr>
            ))}
  
            {/* 합계 행 */}
            <tr className={styles.sumRow}>
              <td>합계</td>
              <td>{comma(sumTotalSalary)}</td>
              <td>{comma(sumIncomeTax)}</td>
              <td>{comma(sumLocalTax)}</td>
              <td>{comma(sumHealth)}</td>
              <td>{comma(sumCare)}</td>
              <td>{comma(sumPension)}</td>
              <td>{comma(sumEmployment)}</td>
              <td>{comma(sumTotal)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}